import {
    AlertTriangle,
    ArrowRight,
    ChevronLeft,
    FileText,
    Info,
    LoaderCircle,
    Sparkles,
    Tag,
    Zap,
} from 'lucide-react'
import React, { useContext, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import { BASE_URL_API } from '../../References/URLs'
import ActiveStoreContext from '../../store/ActiveStoreContext'
import AuthContext from '../../store/auth-context'
import styles from './CreateSalaryStructureScreen.module.css'

const parseActiveStore = (rawStore) => {
    try {
        return rawStore && rawStore.trim() !== '' ? JSON.parse(rawStore) : null
    } catch {
        return null
    }
}

const QUICK_START_TEMPLATES = [
    {
        label: 'Senior Stylist',
        name: 'Senior Stylist',
        description:
            'Experienced stylist with 2+ years. Basic + HRA + Travel Allowance. Commission handled separately via incentive plan.',
        tag: 'Senior Stylist',
    },
    {
        label: 'Trainee / Apprentice',
        name: 'Trainee',
        description:
            'Junior staff member in training period. Fixed stipend only — no HRA. Eligible for basic attendance bonus.',
        tag: 'Trainee',
    },
    {
        label: 'Receptionist / Front Desk',
        name: 'Receptionist',
        description:
            'Front desk staff. Fixed salary with HRA and travel. No service commission applicable.',
        tag: 'Receptionist',
    },
]

const CreateSalaryStructureScreen = () => {
    const navigate = useNavigate()
    const authCtx = useContext(AuthContext)
    const asCtx = useContext(ActiveStoreContext)
    const activeStore = parseActiveStore(asCtx?.activeStore)

    const [formData, setFormData] = useState({
        templateName: '',
        templateDescription: '',
        designationTag: '',
    })
    const [errors, setErrors] = useState({})
    const [isSaving, setIsSaving] = useState(false)

    const handleChange = (field, value) => {
        setFormData((prev) => ({ ...prev, [field]: value }))
        if (errors[field]) setErrors((prev) => ({ ...prev, [field]: undefined }))
    }

    const applyQuickStart = (template) => {
        setFormData({
            templateName: template.name,
            templateDescription: template.description,
            designationTag: template.tag,
        })
        setErrors({})
    }

    const createSalaryTemplate = async () => {
        if (!authCtx?.token || !activeStore?.storeIdentifier) return

        const newErrors = {}

        if (!formData.templateName.trim()) {
            newErrors.templateName = 'Template name is required.'
        } else if (formData.templateName.trim().length > 200) {
            newErrors.templateName = 'Template name must not exceed 200 characters.'
        }

        if (!formData.templateDescription.trim()) {
            newErrors.templateDescription = 'Description is required.'
        }

        if (formData.designationTag.trim().length > 100) {
            newErrors.designationTag = 'Designation tag must not exceed 100 characters.'
        }

        if (Object.keys(newErrors).length > 0) {
            setErrors(newErrors)
            return
        }

        setErrors({})

        try {
            setIsSaving(true)

            const response = await fetch(BASE_URL_API + 'createSalaryStructureTemplate', {
                method: 'POST',
                body: JSON.stringify({
                    token: authCtx.token,
                    storeIdentifier: activeStore.storeIdentifier,
                    templateName: formData.templateName.trim(),
                    templateDescription: formData.templateDescription.trim(),
                    designationTag: formData.designationTag.trim(),
                    createdBy: authCtx?.authIdentifier || '',
                }),
            })

            if (!response.ok) {
                toast.error('Something went wrong. Please try again.')
                return
            }

            const respo = await response.json()

            if (respo?.status === 'success') {
                toast.success('Template created. Now add salary components.')
                navigate(`/payroll/salary-structure/${respo.response.templateIdentifier}`, {
                    replace: true,
                    state: {
                        templateIdentifier: respo.response.templateIdentifier,
                        templateName: respo.response.templateName,
                    },
                })
                return
            }

            if (respo?.message === 'tokenExpired') {
                authCtx.logout()
                return
            }

            switch (respo?.errorCode) {
                case 'VAL_001':
                    if (respo.message.includes('templateName')) {
                        setErrors((prev) => ({ ...prev, templateName: respo.message }))
                    } else if (respo.message.includes('templateDescription')) {
                        setErrors((prev) => ({ ...prev, templateDescription: respo.message }))
                    } else if (respo.message.includes('createdBy')) {
                        toast.error('Session error. Please log out and log back in.')
                    } else {
                        toast.error(respo.message)
                    }
                    break
                case 'VAL_002':
                    if (respo.message.includes('templateName')) {
                        setErrors((prev) => ({ ...prev, templateName: respo.message }))
                    } else if (respo.message.includes('designationTag')) {
                        setErrors((prev) => ({ ...prev, designationTag: respo.message }))
                    }
                    break
                case 'VAL_004':
                    setErrors((prev) => ({
                        ...prev,
                        templateName: `"${formData.templateName.trim()}" already exists. Try a different name.`,
                    }))
                    break
                default:
                    toast.error(respo?.message || 'Failed to create template. Please try again.')
            }
        } catch (error) {
            console.error('createSalaryStructureTemplate error:', error)
            toast.error('Network error. Please check your connection.')
        } finally {
            setIsSaving(false)
        }
    }

    const nameLength = formData.templateName.length
    const descLength = formData.templateDescription.length

    return (
        <div className={styles.page}>

            {/* ── Header ── */}
            <header className={styles.header}>
                <button
                    type="button"
                    className={styles.backBtn}
                    onClick={() => navigate(-1)}
                    disabled={isSaving}
                >
                    <ChevronLeft size={20} />
                </button>
                <div className={styles.headerMeta}>
                    <span className={styles.headerPill}><Sparkles size={13} /> Salary Structures</span>
                    <h1 className={styles.headerTitle}>New Salary Structure</h1>
                    <p className={styles.headerSub}>Define a reusable salary blueprint for a staff tier</p>
                </div>
            </header>

            {/* ── Quick-start tiles ── */}
            <section className={styles.section}>
                <div className={styles.sectionHeader}>
                    <Zap size={14} className={styles.sectionIcon} />
                    <span className={styles.sectionLabel}>Quick-start templates</span>
                </div>
                <div className={styles.quickGrid}>
                    {QUICK_START_TEMPLATES.map((tpl) => (
                        <button
                            key={tpl.name}
                            type="button"
                            className={styles.quickTile}
                            onClick={() => applyQuickStart(tpl)}
                            disabled={isSaving}
                        >
                            <span className={styles.quickTileLabel}>{tpl.label}</span>
                            <span className={styles.quickTileHint}>Tap to auto-fill</span>
                        </button>
                    ))}
                </div>
                <p className={styles.quickNote}>
                    <Info size={12} /> Tapping a tile auto-fills the form — you can still edit before saving.
                </p>
            </section>

            {/* ── Form card ── */}
            <section className={styles.card}>

                {/* Template Name */}
                <div className={`${styles.fieldGroup} ${errors.templateName ? styles.fieldGroupError : ''}`}>
                    <label className={styles.label}>
                        <FileText size={13} /> Template Name <span className={styles.required}>*</span>
                    </label>
                    <input
                        type="text"
                        className={styles.textInput}
                        placeholder="e.g. Senior Stylist, Receptionist, Trainee"
                        value={formData.templateName}
                        maxLength={200}
                        onChange={(e) => handleChange('templateName', e.target.value)}
                    />
                    {nameLength > 150 && (
                        <p className={`${styles.charCounter} ${nameLength >= 190 ? styles.charCounterWarn : ''}`}>
                            {nameLength} / 200
                        </p>
                    )}
                    {errors.templateName && (
                        <p className={styles.errorText}><AlertTriangle size={13} /> {errors.templateName}</p>
                    )}
                </div>

                {/* Description */}
                <div className={`${styles.fieldGroup} ${errors.templateDescription ? styles.fieldGroupError : ''}`}>
                    <label className={styles.label}>
                        Description <span className={styles.required}>*</span>
                    </label>
                    <textarea
                        className={styles.textarea}
                        rows={4}
                        placeholder="Describe who this structure is for and what components it includes..."
                        value={formData.templateDescription}
                        onChange={(e) => handleChange('templateDescription', e.target.value)}
                    />
                    {errors.templateDescription && (
                        <p className={styles.errorText}><AlertTriangle size={13} /> {errors.templateDescription}</p>
                    )}
                </div>

                {/* Designation Tag */}
                <div className={`${styles.fieldGroup} ${errors.designationTag ? styles.fieldGroupError : ''}`}>
                    <label className={styles.label}>
                        <Tag size={13} /> Designation Tag
                        <span className={styles.optionalBadge}>Optional</span>
                    </label>
                    <p className={styles.fieldHint}>Helps filter templates by role in the salary structure list.</p>
                    <input
                        type="text"
                        className={styles.textInput}
                        placeholder="e.g. Senior Stylist, Manager"
                        value={formData.designationTag}
                        maxLength={100}
                        onChange={(e) => handleChange('designationTag', e.target.value)}
                    />
                    {errors.designationTag && (
                        <p className={styles.errorText}><AlertTriangle size={13} /> {errors.designationTag}</p>
                    )}
                </div>
            </section>

            {/* ── What happens next ── */}
            <section className={styles.nextStepsBox}>
                <p className={styles.nextStepsTitle}>What happens after you create this template?</p>
                <ol className={styles.nextStepsList}>
                    <li>You'll land directly on the <strong>template detail screen</strong>.</li>
                    <li>Add salary components — Basic, HRA, Travel Allowance, PF, ESI, PT, etc.</li>
                    <li>Assign the template to staff members when it's ready.</li>
                </ol>
            </section>

            {/* ── Save button ── */}
            <button
                type="button"
                className={`${styles.saveBtn} ${isSaving ? styles.saveBtnDisabled : ''}`}
                onClick={createSalaryTemplate}
                disabled={isSaving}
            >
                {isSaving
                    ? <><LoaderCircle size={16} className={styles.spin} /> Creating…</>
                    : <>Create Template <ArrowRight size={16} /></>}
            </button>
        </div>
    )
}

export default CreateSalaryStructureScreen